"use client";

import { startTransition } from "react";
import { usePathname, useRouter } from "next/navigation";
import GovernanceError from "@/app/[slug]/governance/error";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { WorkspaceRouteProvider } from "@/components/workspaces/workspace-route-context";
import { GovernanceWorkspaceShell } from "@/views/governance/GovernanceWorkspaceShell";

export default function ApplicationError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  const pathname = usePathname();
  const router = useRouter();
  const slug = governanceRouteSlug(pathname);
  const retry = () => {
    // Reset alone replays the rejected server tree; refresh requests new inputs.
    startTransition(() => {
      router.refresh();
      reset();
    });
  };

  if (slug) {
    return (
      <WorkspaceRouteProvider namespaceId={null} slug={slug}>
        <GovernanceWorkspaceShell><GovernanceError error={error} reset={retry} /></GovernanceWorkspaceShell>
      </WorkspaceRouteProvider>
    );
  }

  return (
    <main className="flex flex-col items-start gap-[var(--space-md)] p-[var(--space-lg)]">
      <Alert>
        <AlertTitle>Couldn’t load this page</AlertTitle>
        <AlertDescription>Try again to reload this page.</AlertDescription>
      </Alert>
      <Button variant="outline" onClick={retry}>Try again</Button>
    </main>
  );
}

function governanceRouteSlug(pathname: string | null): string | null {
  const [, encodedSlug, section] = pathname?.split("/") ?? [];
  if (!encodedSlug || section !== "governance") return null;
  try {
    return decodeURIComponent(encodedSlug);
  } catch {
    // A malformed URL must not throw again while rendering the error boundary.
    return null;
  }
}
